import { Badge } from '@antimatterhq/ui/components/badge';
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableRow,
} from '@antimatterhq/ui/components/simpleTable';
import { Form, FormikProvider, useFormik, useFormikContext } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { RowButton } from '@/components/button/row/RowButton';
import { FormInput } from '@/components/form/form';
import {
  getManagedRetentionInitialOverrideValues,
  setManagedRetentionOverride,
} from '@/lib/workspaceConfig/managedRetention';
import { WorkspaceConfig_ManagedRetention_Override } from '@/types/api';
import {
  ManagedRetentionOverrideSchema,
  ManagedRetentionOverrideSchemaValues,
} from '@/zod/schemas/workspaceConfig/ManagedRetentionSchema';

const Row = ({ onCancel }: { onCancel: () => void }) => {
  const { values, handleChange, handleBlur } =
    useFormikContext<ManagedRetentionOverrideSchemaValues>();
  return (
    <SimpleTable>
      <SimpleTableBody>
        <SimpleTableRow>
          <SimpleTableCell className="w-[250px]">
            <div className="flex items-center gap-2">
              <Badge variant="filled" color="info">
                Override
              </Badge>
              <FormInput
                name="table"
                value={values.table}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </SimpleTableCell>
          <SimpleTableCell className="w-auto">--</SimpleTableCell>
          <SimpleTableCell className="w-[150px]">
            <FormInput
              name="column"
              value={values.column}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </SimpleTableCell>
          <SimpleTableCell className="w-[150px]">
            <FormInput
              name="duration"
              value={values.duration}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </SimpleTableCell>
          <SimpleTableCell className="w-[150px]">
            <div className="flex flex-row justify-end gap-2">
              <RowButton type="submit" icon="lucide:check" />
              <RowButton icon="lucide:x" onClick={onCancel} />
            </div>
          </SimpleTableCell>
        </SimpleTableRow>
      </SimpleTableBody>
    </SimpleTable>
  );
};

export type EditManagedRetentionOverrideRowProps = {
  override: WorkspaceConfig_ManagedRetention_Override;
  onSave: (newRow: WorkspaceConfig_ManagedRetention_Override) => void;
  onCancel: () => void;
};

export const EditManagedRetentionOverrideRow = ({
  override,
  onSave,
  onCancel,
}: EditManagedRetentionOverrideRowProps) => {
  const formik = useFormik({
    initialValues: getManagedRetentionInitialOverrideValues(override),
    validationSchema: toFormikValidationSchema(ManagedRetentionOverrideSchema),
    validate: toFormikValidate(ManagedRetentionOverrideSchema),
    onSubmit: async (newValues) => {
      const newRow = setManagedRetentionOverride(newValues);
      onSave(newRow);
    },
  });

  return (
    <FormikProvider value={formik}>
      <Form>
        <Row onCancel={onCancel} />
      </Form>
    </FormikProvider>
  );
};
